import { cn } from './utils';

/**
 * Color-coded badge for asset_lifecycle_state. Values come from the
 * seeded reference table (active / idle / in_transit / decommissioned /
 * disposed / quarantined). Unknown ids render as a plain neutral pill so
 * future states slot in safely without code changes.
 */

const STYLES: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  idle: 'bg-muted text-foreground ring-border',
  in_transit: 'bg-sky-50 text-sky-700 ring-sky-200',
  decommissioned: 'bg-muted text-muted-foreground ring-border',
  disposed: 'bg-red-50 text-red-700 ring-red-200',
  quarantined: 'bg-amber-50 text-amber-700 ring-amber-200',
};

// Canonical unknown/fallback style — keep in sync with ConditionBadge's
// fallback. Do not diverge; this was previously drifted across copies.
const FALLBACK_STYLE = 'bg-muted text-muted-foreground ring-border';

const LABELS: Record<string, string> = {
  active: 'Active',
  idle: 'Idle',
  in_transit: 'In transit',
  decommissioned: 'Decommissioned',
  disposed: 'Disposed',
  quarantined: 'Quarantined',
};

/** Human label for a lifecycle state id — for selects and non-badge surfaces
 * that only have the id (the badge itself takes the DB reference name). */
export function lifecycleLabel(id: string): string {
  return LABELS[id] ?? id;
}

export function LifecycleBadge({
  id,
  label,
}: {
  id: string;
  label: string;
}) {
  const style = STYLES[id] ?? FALLBACK_STYLE;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        style,
      )}
    >
      {label}
    </span>
  );
}
